import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Column } from 'primereact/column'
import DataTable from '../../../components/ui/DataTable'
import EmptyState from '../../../components/ui/EmptyState'
import Button from '../../../components/ui/Button'
import { useToast } from '../../../components/ui/ToastProvider'
import { useGrants } from '../../../auth/useCurrentUser'
import { GRANTS } from '../../../auth/grants'
import { CARD_VIEW_BREAKPOINT, useMediaQuery } from '../../../lib/useMediaQuery'
import { formatDateTime } from '../../../lib/datetime'
import { downloadMinutesReportPdf } from '../../minutes/api/minutesApi'
import MeetingStatusBadge from './MeetingStatusBadge'
import { MEETING_STATUS, type MeetingListItem } from '../types'

interface Props {
  items: MeetingListItem[]
  loading: boolean
  /** Satıra tıklanınca (Toplantı Detay'a git — 044). */
  onSelect?: (id: number) => void
  /** Durum (Taslak/Yayınlandı) kolonu — drill-down'da taslağı ayırt etmek için. */
  showStatus?: boolean
  /** Grup adı kolonu — arşiv ekranı gibi çoklu-grup listelerinde. */
  showGroup?: boolean
  /** Boş-durum metni (varsayılan grup drill-down metnidir; Taslaklar gibi ekranlar kendi metnini verir). */
  emptyTitle?: string
  emptyDesc?: string
  /** Sunucu-taraflı sıralama (verilirse kolonlar sortable + lazy); parent sortField/sortOrder'ı sorguya taşır. */
  sortField?: string
  sortOrder?: 1 | -1 | 0 | null
  onSort?: (e: { sortField: string; sortOrder: 1 | -1 | 0 | null | undefined }) => void
  /** Sunucu sayfalamasının toplam kayıt sayısı — lazy (sıralanabilir) tabloda paginatör raporu ("N kayıttan
   *  X-Y") için; verilmezse lazy modda PrimeReact totalRecords'u bilmediğinden "null kayıttan" gösterir. */
  totalRecords?: number
}

/**
 * Grup drill-down + arşiv toplantı listesi (038 + 044): numara · ad · [grup] · tarih · yer · [durum].
 * İlk kolon toplantı numarasıdır (analiz 12; ör. `TK-2026-0145`) — numara yayında atandığından
 * taslak satırda boş kalır (yer tutucu yazılmaz, Page Standards).
 */
export default function MeetingList({
  items,
  loading,
  onSelect,
  showStatus,
  showGroup,
  emptyTitle,
  emptyDesc,
  sortField,
  sortOrder,
  onSort,
  totalRecords,
}: Readonly<Props>) {
  const { t } = useTranslation()
  const toast = useToast()
  const { hasGrant } = useGrants()
  // 077 — Toplantı No kolonu yalnız RECORD_NUMBER_VIEW yetkisinde (Özel Kalem).
  const showRecordNumber = hasGrant(GRANTS.RECORD_NUMBER_VIEW)
  const sortable = !!onSort

  // Rapor Al: yalnız Yayınlandı (361) toplantıda anlamlı (Toplantı Detay ile tutarlı). Liste yayınlanmış
  // toplantı içeriyorsa (grup drill-down) rapor kolonu gösterilir; taslak/arşiv listelerinde otomatik gizli.
  // Yetki (075): uç MEETING_REPORT_VIEW ister — Toplantı Detay'daki butonla AYNI kapı. Bu kolon gate'siz
  // kalmıştı: yetkisiz kullanıcı butonu görüyor, tıklayınca 403 → genel "Rapor oluşturulamadı" toast'ı
  // alıyordu (ör. Yönetici rolü; Genel Müdür'e yetki 075b seed'iyle sonradan verildi).
  const [reportingId, setReportingId] = useState<number | null>(null)
  const hasReport =
    hasGrant(GRANTS.MEETING_REPORT_VIEW) &&
    items.some((m) => m.meetingStatusId === MEETING_STATUS.PUBLISHED)

  const onReport = async (id: number) => {
    setReportingId(id)
    try {
      await downloadMinutesReportPdf(id)
    } catch {
      toast.error(t('meetings.detail.report.error'))
    } finally {
      setReportingId(null)
    }
  }

  // Dar ekranda (eşik: CARD_VIEW_BREAKPOINT — lib/useMediaQuery) tablo yerine kart listesi (bkz. ui.css `.ui-card-list`) — sayfalama
  // BU bileşenin dışında (çağıran `<Paginator>` render eder) olduğundan mobil dalın kendi sayfalama
  // mantığına ihtiyacı yok — aynı `items` + parent'ın paginatörü her iki dalda da geçerlidir. Bu
  // bileşen PAYLAŞILDIğı (`GroupMeetingsPage` + `MinutesArchivedMeetingsPage`) için tek düzeltme iki
  // ekrana birden yayılır (Reuse Mandate).
  const isMobile = useMediaQuery(CARD_VIEW_BREAKPOINT)

  if (isMobile) {
    if (loading) {
      return (
        <div className="ui-card-list">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="ui-card ui-card--skeleton" aria-hidden />
          ))}
        </div>
      )
    }
    if (items.length === 0) {
      return (
        <EmptyState
          icon="pi pi-calendar-times"
          title={emptyTitle ?? t('meetings.detail.empty.title')}
          description={emptyDesc ?? t('meetings.detail.empty.desc')}
        />
      )
    }
    return (
      <div className="ui-card-list">
        {items.map((row) => (
          // 15.09.2026 — **overlay-link**: kart kökü handler'sız, detayı başlıktaki gerçek `<button>`
          // açıyor ve kartı `::after` ile kaplıyor (`ui.css` → `.ui-card--linked`). "Rapor" düğmesi o
          // kontrolün KARDEŞİdir. Önceki biçimde kart `role="button"` taşıyor ve Rapor onun İÇİNDE
          // kalıyordu; ARIA'da interaktif kontrol başka bir interaktif kontrol içeremez (THY inceleme
          // bulgusu, 15.09.2026; anayasa → kart kuralı). `e.target === e.currentTarget` elemesi yalnız
          // KLAVYE davranışını düzeltiyordu, semantiği değil — kardeşlikle elemeye gerek kalmadı.
          <div key={row.id} className={`ui-card${onSelect ? ' ui-card--linked' : ''}`}>
            <div className="ui-card-head">
              {onSelect ? (
                <button type="button" className="ui-card-link" onClick={() => onSelect(row.id)}>
                  <span className="ui-card-title">{row.name}</span>
                </button>
              ) : (
                <span className="ui-card-title">{row.name}</span>
              )}
              {showStatus && <MeetingStatusBadge statusId={row.meetingStatusId} />}
            </div>
            {showRecordNumber && row.recordNumber && (
              <span className="ui-card-sub">{row.recordNumber}</span>
            )}
            <div className="ui-card-row">
              <span>{formatDateTime(row.meetingDate)}</span>
              {row.meetingLocation && <span>{row.meetingLocation}</span>}
              {showGroup && row.groupName && <span>{row.groupName}</span>}
            </div>
            {hasReport && row.meetingStatusId === MEETING_STATUS.PUBLISHED && (
              <div className="ui-card-actions">
                <Button
                  text
                  severity="secondary"
                  size="small"
                  icon="pi pi-file-pdf"
                  label={t('meetings.detail.report.action')}
                  loading={reportingId === row.id}
                  // `stopPropagation` KALDIRILDI (15.09.2026): kart kökünde artık handler yok,
                  // kabarcıklanacak bir ata kalmadı. Sonar S1082/S6848 yüzeyi de böylece kapanıyor.
                  onClick={() => onReport(row.id)}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    )
  }

  return (
    <DataTable
      value={items}
      loading={loading}
      lazy={sortable}
      sortField={sortField}
      sortOrder={sortOrder}
      onSort={onSort}
      totalRecords={totalRecords}
      onRowClick={onSelect ? (e) => onSelect((e.data as MeetingListItem).id) : undefined}
      rowClassName={onSelect ? () => 'mtg-row-clickable' : undefined}
      emptyMessage={
        <EmptyState
          icon="pi pi-calendar-times"
          title={emptyTitle ?? t('meetings.detail.empty.title')}
          description={emptyDesc ?? t('meetings.detail.empty.desc')}
        />
      }
    >
      {showRecordNumber && (
        <Column
          field="recordNumber"
          header={t('meetings.detail.columns.number')}
          sortable={sortable}
          style={{ width: '11rem' }}
        />
      )}
      <Column
        field="name"
        header={t('meetings.detail.columns.name')}
        sortable={sortable}
        bodyClassName="tk-cell-strong"
      />
      {showGroup && (
        <Column
          header={t('meetings.detail.info.group')}
          sortable={sortable}
          sortField="groupName"
          body={(row: MeetingListItem) => row.groupName ?? ''}
        />
      )}
      <Column
        header={t('meetings.detail.columns.date')}
        sortable={sortable}
        sortField="meetingDate"
        body={(row: MeetingListItem) => formatDateTime(row.meetingDate)}
      />
      <Column
        field="meetingLocation"
        header={t('meetings.detail.columns.location')}
        sortable={sortable}
      />
      {showStatus && (
        <Column
          header={t('meetings.status.label')}
          sortable={sortable}
          sortField="meetingStatusId"
          body={(row: MeetingListItem) => <MeetingStatusBadge statusId={row.meetingStatusId} />}
        />
      )}
      {hasReport && (
        <Column
          header=""
          style={{ width: '5rem' }}
          bodyStyle={{ textAlign: 'center' }}
          body={(row: MeetingListItem) =>
            row.meetingStatusId === MEETING_STATUS.PUBLISHED ? (
              <Button
                text
                severity="secondary"
                size="small"
                icon="pi pi-file-pdf"
                aria-label={t('meetings.detail.report.action')}
                tooltip={t('meetings.detail.report.action')}
                tooltipOptions={{ position: 'top' }}
                loading={reportingId === row.id}
                // Satır tıklaması (detaya git) tetiklenmesin.
                onClick={(e) => {
                  e.stopPropagation()
                  onReport(row.id)
                }}
              />
            ) : null
          }
        />
      )}
    </DataTable>
  )
}
